import React, { useMemo, useState } from 'react';
import {
    useGetRecentProjectCreationsQuery, useGetRecentModelCreationsQuery,
    useGetRecentClientCreationsQuery, useGetRecentLibraryCreationsQuery,
    useGetRecentContentCreationsQuery,
    useGetUsersShortQuery,
} from '../../../generated/graphql';
import { useCurrentUser } from '../../../App.hooks';
import { FolderKanban, Box, Clock, Inbox, Sparkles, Users, LibraryBig, FileText, UserCircle } from 'lucide-react';

/** Convert epoch-seconds string → relative time label */
function timeAgo(epochStr: string | null | undefined): string {
    if (!epochStr) return '';
    const seconds = Number(epochStr);
    if (isNaN(seconds)) return '';
    const now = Math.floor(Date.now() / 1000);
    const diff = now - seconds;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    const d = new Date(1970, 0, 1);
    d.setUTCSeconds(seconds);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

type CreationItem = {
    id: string;
    type: 'client' | 'library' | 'project' | 'model' | 'content';
    name: string;
    creatorName: string;
    creatorId: string | null;
    time: string;
    timeRaw: number;
};

type RecentCreationsPanelProps = {
    maxItems?: number;
};

const TYPE_CONFIG = {
    client: {
        label: 'Client',
        icon: Users,
        color: 'var(--accent)',
        bgColor: 'var(--accent-soft)',
    },
    library: {
        label: 'Library',
        icon: LibraryBig,
        color: '#8b5cf6',
        bgColor: '#f5f3ff',
    },
    project: {
        label: 'Project',
        icon: FolderKanban,
        color: 'var(--success)',
        bgColor: 'var(--success-soft, #f0fdf4)',
    },
    model: {
        label: 'Model',
        icon: Box,
        color: 'var(--info)',
        bgColor: 'var(--info-soft, #eff6ff)',
    },
    content: {
        label: 'Content',
        icon: FileText,
        color: 'var(--warning)',
        bgColor: 'var(--warning-soft, #fffbeb)',
    },
};

const RecentCreationsPanel = ({ maxItems = 20 }: RecentCreationsPanelProps) => {
    const currentUser = useCurrentUser();
    const [myOnly, setMyOnly] = useState(false);

    const clientsQuery = useGetRecentClientCreationsQuery({
        variables: { first: maxItems },
        fetchPolicy: 'cache-and-network',
    });
    const librariesQuery = useGetRecentLibraryCreationsQuery({
        variables: { first: maxItems },
        fetchPolicy: 'cache-and-network',
    });
    const projectsQuery = useGetRecentProjectCreationsQuery({
        variables: { first: maxItems },
        fetchPolicy: 'cache-and-network',
    });
    const modelsQuery = useGetRecentModelCreationsQuery({
        variables: { first: maxItems },
        fetchPolicy: 'cache-and-network',
    });
    const contentsQuery = useGetRecentContentCreationsQuery({
        variables: { first: maxItems },
        fetchPolicy: 'cache-and-network',
    });

    // Fallback user lookup for entries where createdBy.name is missing
    const usersQuery = useGetUsersShortQuery();
    const userNameById = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        for (const u of usersQuery.data?.UserList?.nodes || []) {
            if (u?.id && u?.name) map[u.id] = u.name;
        }
        return map;
    }, [usersQuery.data]);

    const isLoading =
        (clientsQuery.loading && !clientsQuery.data) ||
        (librariesQuery.loading && !librariesQuery.data) ||
        (projectsQuery.loading && !projectsQuery.data) ||
        (modelsQuery.loading && !modelsQuery.data) ||
        (contentsQuery.loading && !contentsQuery.data);

    const items: CreationItem[] = useMemo(() => {
        const all: CreationItem[] = [];

        const resolve = (createdBy: { id?: string | null; name?: string | null } | null | undefined, createdById?: string | null): string => {
            if (createdBy?.name) return createdBy.name;
            const id = createdBy?.id || createdById;
            if (id && userNameById[id]) return userNameById[id];
            return 'Unknown';
        };

        for (const node of clientsQuery.data?.ClientList?.nodes || []) {
            if (!node) continue;
            all.push({
                id: `client-${node.id}`,
                type: 'client',
                name: node.name || 'Untitled',
                creatorName: resolve(node.createdBy, node.createdById),
                creatorId: node.createdBy?.id || node.createdById || null,
                time: timeAgo(node.createdTime),
                timeRaw: Number(node.createdTime) || 0,
            });
        }

        for (const node of librariesQuery.data?.LibraryList?.nodes || []) {
            if (!node) continue;
            all.push({
                id: `library-${node.id}`,
                type: 'library',
                name: node.name || 'Untitled',
                creatorName: resolve(node.createdBy, node.createdById),
                creatorId: node.createdBy?.id || node.createdById || null,
                time: timeAgo(node.createdTime),
                timeRaw: Number(node.createdTime) || 0,
            });
        }

        for (const node of projectsQuery.data?.ProjectList?.nodes || []) {
            if (!node) continue;
            all.push({
                id: `project-${node.id}`,
                type: 'project',
                name: node.name || 'Untitled',
                creatorName: resolve(node.createdBy, node.createdById),
                creatorId: node.createdBy?.id || node.createdById || null,
                time: timeAgo(node.createdTime),
                timeRaw: Number(node.createdTime) || 0,
            });
        }

        for (const node of modelsQuery.data?.ModelList?.nodes || []) {
            if (!node) continue;
            all.push({
                id: `model-${node.id}`,
                type: 'model',
                name: node.name || 'Untitled',
                creatorName: resolve(node.createdBy, node.createdById),
                creatorId: node.createdBy?.id || node.createdById || null,
                time: timeAgo(node.createdTime),
                timeRaw: Number(node.createdTime) || 0,
            });
        }

        for (const node of contentsQuery.data?.ContentList?.nodes || []) {
            if (!node) continue;
            all.push({
                id: `content-${node.id}`,
                type: 'content',
                name: node.name || 'Untitled',
                creatorName: resolve(node.createdBy, node.createdById),
                creatorId: node.createdBy?.id || node.createdById || null,
                time: timeAgo(node.createdTime),
                timeRaw: Number(node.createdTime) || 0,
            });
        }

        all.sort((a, b) => b.timeRaw - a.timeRaw);

        const filtered = myOnly && currentUser?.id
            ? all.filter(item => item.creatorId === currentUser.id)
            : all;

        return filtered.slice(0, maxItems);
    }, [clientsQuery.data, librariesQuery.data, projectsQuery.data, modelsQuery.data, contentsQuery.data, maxItems, userNameById, myOnly, currentUser?.id]);

    return (
        <div className="card p-0 overflow-hidden animate-slide-up" style={{ width: '100%' }}>
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-default)' }}>
                <div className="flex items-center gap-2">
                    <Sparkles size={16} style={{ color: 'var(--accent)' }} />
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Recent Creations</span>
                    {items.length > 0 && (
                        <span
                            className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                            style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}
                        >
                            {items.length}
                        </span>
                    )}
                </div>
                <button
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium transition-all duration-150"
                    style={{
                        backgroundColor: myOnly ? 'var(--accent-soft)' : 'transparent',
                        color: myOnly ? 'var(--accent)' : 'var(--text-tertiary)',
                        border: `1px solid ${myOnly ? 'var(--accent)' : 'var(--border-default)'}`,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                    }}
                    onClick={() => setMyOnly(prev => !prev)}
                >
                    <UserCircle size={12} />
                    My Activity
                </button>
            </div>

            {/* Loading skeleton */}
            {isLoading && (
                <div className="p-5 space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="flex gap-3">
                            <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ background: 'var(--bg-subtle)' }} />
                            <div className="flex-1 space-y-2">
                                <div className="h-3 w-3/4 rounded" style={{ background: 'var(--bg-subtle)' }} />
                                <div className="h-3 w-1/2 rounded" style={{ background: 'var(--bg-subtle)' }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && items.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 px-5">
                    <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                        style={{ backgroundColor: 'var(--bg-subtle)' }}
                    >
                        <Inbox size={24} style={{ color: 'var(--text-tertiary)' }} />
                    </div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>No creations yet</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                        Recently created clients, libraries, projects and models will appear here
                    </p>
                </div>
            )}

            {/* Creations list */}
            {!isLoading && items.length > 0 && (
                <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {items.map((item, index) => {
                        const config = TYPE_CONFIG[item.type];
                        const TypeIcon = config.icon;

                        return (
                            <div
                                key={item.id}
                                className="px-5 py-3 flex items-center gap-3"
                                style={{
                                    borderBottom: index < items.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                                }}
                            >
                                {/* Avatar */}
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold"
                                    style={{ backgroundColor: config.bgColor, color: config.color }}
                                >
                                    {item.creatorName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm leading-snug" style={{ color: 'var(--text-primary)' }}>
                                        <span className="font-semibold">{item.creatorName}</span>
                                        <span style={{ color: 'var(--text-secondary)' }}> created a </span>
                                        <span
                                            className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium"
                                            style={{ backgroundColor: config.bgColor, color: config.color }}
                                        >
                                            <TypeIcon size={10} />
                                            {config.label}
                                        </span>
                                    </p>
                                </div>

                                {/* Timestamp */}
                                <span
                                    className="text-xs whitespace-nowrap flex items-center gap-1 flex-shrink-0"
                                    style={{ color: 'var(--text-tertiary)' }}
                                >
                                    <Clock size={11} />
                                    {item.time}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RecentCreationsPanel;
